import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

function PiggybackModal({ show, handleClose, orderToEdit, onSubmit }) {
  const [shippingMethod, setShippingMethod] = useState('');
  const [piggybackOption, setPiggybackOption] = useState('next'); // 'next' | 'custom'
  const [piggybackOrderName, setPiggybackOrderName] = useState('');
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [modalMessage, setModalMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Parse existing shipping method
  const parsePiggyback = (method) => {
    if (!method) return { base: '', option: 'next', name: '' };
    if (method.startsWith('Piggyback with')) {
      const rest = method.replace('Piggyback with', '').trim();
      if (rest.toLowerCase() === 'next cover') return { base: 'Piggyback', option: 'next', name: '' };
      return { base: 'Piggyback', option: 'custom', name: rest };
    }
    return { base: method, option: 'next', name: '' };
  };

  // Prefill when modal opens
  useEffect(() => {
    if (!orderToEdit) return;
    const { base, option, name } = parsePiggyback(orderToEdit.shippingMethod);
    setShippingMethod(base || '');
    setPiggybackOption(option);
    setPiggybackOrderName(name || '');
    setTouched({});
    setErrors({});
    setModalMessage('');
  }, [orderToEdit]);

  const validate = () => {
    const e = {};
    if (!shippingMethod) e.shippingMethod = 'Shipping Method is required.';
    if (shippingMethod === 'Piggyback' && piggybackOption === 'custom') {
      if (!piggybackOrderName.trim()) e.piggybackOrderName = 'Enter the specific order name for piggyback.';
      else if (piggybackOrderName.trim().length < 2) e.piggybackOrderName = 'Must be at least 2 characters.';
    }
    setErrors(e);
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ shippingMethod: true, piggybackOrderName: true });
    const eObj = validate();
    if (Object.keys(eObj).length > 0) {
      setModalMessage('Please correct the highlighted fields.');
      return;
    }

    setLoading(true);
    setModalMessage('');

    let finalMethod = shippingMethod;
    if (shippingMethod === 'Piggyback') {
      finalMethod = piggybackOption === 'next'
        ? 'Piggyback with Next Cover'
        : `Piggyback with ${piggybackOrderName.trim()}`;
    }

    try {
      await onSubmit(finalMethod); // Expect parent to handle API call
      handleClose(true);
    } catch (err) {
      console.error(err);
      setModalMessage('Failed to update shipping method.');
    } finally {
      setLoading(false);
    }
  };

  const invalid = (name) => !!(touched[name] && errors[name]);
  const feedback = (name) => (invalid(name) ? errors[name] : '');

  return (
    <Modal show={show} onHide={() => handleClose(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Shipping Method</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modalMessage && <Alert variant="danger">{modalMessage}</Alert>}

        <Form onSubmit={handleSubmit} noValidate>
          <Form.Group className="mb-3">
            <Form.Label>Shipping Method</Form.Label>
            <Form.Select
              value={shippingMethod}
              onChange={(e) => setShippingMethod(e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, shippingMethod: true }))}
              isInvalid={invalid('shippingMethod')}
              disabled={loading}
            >
              <option value="" disabled>Select Shipping Method</option>
              <option value="Postal Service">Postal Service</option>
              <option value="LTL">LTL</option>
              <option value="Piggyback">Piggyback</option>
              <option value="Pickup">Pickup</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">{feedback('shippingMethod')}</Form.Control.Feedback>
          </Form.Group>

          {shippingMethod === 'Piggyback' && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="radio"
                  label="Piggyback with Next Cover"
                  checked={piggybackOption === 'next'}
                  onChange={() => setPiggybackOption('next')}
                  disabled={loading}
                />
                <Form.Check
                  type="radio"
                  label="Piggyback with Specific Order"
                  checked={piggybackOption === 'custom'}
                  onChange={() => setPiggybackOption('custom')}
                  disabled={loading}
                />
              </Form.Group>

              {piggybackOption === 'custom' && (
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Enter Order Name"
                    value={piggybackOrderName}
                    onChange={(e) => setPiggybackOrderName(e.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, piggybackOrderName: true }))}
                    isInvalid={invalid('piggybackOrderName')}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">{feedback('piggybackOrderName')}</Form.Control.Feedback>
                </Form.Group>
              )}
            </>
          )}

          <Button variant="primary" type="submit" className="w-100" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Update'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default PiggybackModal;
